/* eslint-disable no-empty */
import { PERMISSIONS } from '@/constants/permissions';
import { checkAllowanceLimit } from '@/services/affiliates.service';
import {
  getFile,
  getFilePayments,
  getFileAttachemnts,
  hasPendingDocuments,
  getFileTicketAttachemnts,
  getFileReceiptAttachemnts,
  getFileVoucherAttachemnts,
  getTramitAttachments,
  getVoucherTypes,
  getAffiliateFile,
  getTramitTypes,
  getReceiptTypes,
  getDocumentTypes,
  getFileOdontograms,
  getFileErrors,
  getDispositions,
  uploadDisosition,
  getFilesPrivateAttachments,
  getFilesValidations,
  getCoveragesPerFile,
  getAuthorization,
} from '@/services/files.service';
import { hasPermission, hasPermissions } from '@/services/login.service';
import { notification } from 'antd';

import { history } from 'umi';
import { redirectNotFound } from '@/utils/errorRedirect';

const Model = {
  namespace: 'fileDetails',
  state: {
    file: null,
    tickets: [],
    receipts: [],
    voucherTypes: [],
    vouchers: [],
    payments: [],
    fileErrors: [],
    attachments: [],
    odontograms: [],
    tramitTypes: [],
    resolutions: [],
    dispositions: [],
    authorizations: [],
    receiptTypes: [],
    documentTypes: [],
    fileValidations: [],
    filePrivateAttachments: [],
    isLoadingFiles: true,
    pendingDocuments: false,
    coverages: [],
    affiliatePartialFiles: [],
  },

  effects: {
    *fetchTypeData(_, { put, all }) {
      yield all([
        put({ type: 'fetchVoucherTypes' }),
        put({ type: 'fetchReceiptTypes' }),
        put({ type: 'fetchFileTypes' }),
        ]);
      },

    *fetchFile({ payload }, { call, put }) {
      const { id, affiliate } = payload;

      let file;
      let pendingDocuments = null;
      let limit = null;
      let limitError = null;

      try {
        const fileResponse = affiliate
          ? yield call(getAffiliateFile, affiliate, id)
          : yield call(getFile, id);

        file = fileResponse.data;
      } catch (error) {
        console.error('Error obteniendo expediente:', error);
        const status = error?.status || error?.response?.status;

        if (status === 404) {
          history.push('/404');
          return;
        }

        yield put({
          type: 'show',
          payload: {
            error: {
              type: 'file',
              message:
                error?.data?.message ||
                error?.response?.data?.message ||
                error?.message ||
                'Ocurrió un error al cargar el expediente.',
            },
          },
        });
        return;
      }

      if (!affiliate) {
        try {
          const pendingDocumentsResponse = yield call(
            hasPendingDocuments,
            id,
          );
          pendingDocuments = pendingDocumentsResponse.data;
        } catch (error) {
          console.warn(
            'Error obteniendo documentos pendientes:',
            error,
          );
        }
      }

      if (!affiliate) {
        try {
          const limitResponse = yield call(
            checkAllowanceLimit,
            file.affiliate.id,
            {
              file: id,
              period: file.period,
              allowance_id: file.allowance.id,
              allowance_amount: file.allowance_amount,
              amount_requested: file.amount_awarded
                ? Number(file.amount_awarded) + file.pharmacy_amount
                : Number(file.amount_requested || 0) + file.pharmacy_amount,
            },
          );

          limit = limitResponse.data;

          if (file.tramit_type?.key === 'EXCEPTION') {
            limit.result = true;
          }
        } catch (error) {
          console.error('Error verificando límite:', error);

          limitError = {
            result: false,
            message:
              error?.data?.message ||
              error?.response?.data?.message ||
              error?.message ||
              'No se pudo verificar el límite de la prestación.',
          };
        }
      }

      yield put({
        type: 'show',
        payload: {
          file,
          limit: limit ?? limitError,
          pendingDocuments,
        },
      });
    },

    *fetchCoverages({ payload }, { call, put }) {
      try {
        const response = yield call(
          getCoveragesPerFile,
          payload.id,
        );

        yield put({
          type: 'show',
          payload: {
            coverages: response.data ?? [],
          },
        });
      } catch (error) {
        console.warn('Error al cargar las coberturas', error);
      }
    },

    *addOdontogram({ payload }, { put }) {
      yield put({
        type: 'show',
        payload: { odontograms: [payload] },
      });
    },

    *fetchFileTypes(_, { call, put }) {
      try {
        const response = yield call(getTramitTypes);
        yield put({
          type: 'show',
          payload: { tramitTypes: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchVouchersAttachments(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ fileDetails }) => fileDetails.file);
        const affiliate = selectedFile.affiliate.id;
        const response = yield call(getFileVoucherAttachemnts, affiliate, selectedFile.id);
        yield put({
          type: 'show',
          payload: { vouchers: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchReceiptsAttachments(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ fileDetails }) => fileDetails.file);
        const affiliate = selectedFile.affiliate.id;
        const response = yield call(getFileReceiptAttachemnts, affiliate, selectedFile.id);
        yield put({
          type: 'show',
          payload: { receipts: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchTicketsAttachments(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ fileDetails }) => fileDetails.file);
        const affiliate = selectedFile.affiliate.id;

        const response = yield call(getFileTicketAttachemnts, affiliate, selectedFile.id);
        yield put({
          type: 'show',
          payload: { tickets: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchAffiliatePartialFiles(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ fileDetails }) => fileDetails.file);
        const affiliatePartialFiles = yield call(getTramitAttachments, selectedFile.id);
        yield put({
          type: 'show',
          payload: { affiliatePartialFiles },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchFileErrors(_, { call, put }) {
      const response = yield call(getFileErrors);
      yield put({
        type: 'show',
        payload: { fileErrors: response.data },
      });
    },

    *fetchReceiptTypes(_, { all, call, put }) {
      try {
        const [recResponse, docResponse] = yield all([
          call(getReceiptTypes),
          call(getDocumentTypes),
        ]);


        yield put({
          type: 'show',
          payload: {
            receiptTypes: recResponse?.data ?? [],
            documentTypes: docResponse ?? [],
          },
        });

      } catch (error) {
        notification.error({ message: error?.response?.data?.detail ?? 'Ocurrió un error al cargar los datos de esta pantalla.' });
      }
    },

    *fetchVoucherTypes(_, { all, call, put }) {
      try {
        const [response] = yield all([call(getVoucherTypes)]);
        yield put({
          type: 'show',
          payload: { voucherTypes: response?.data ?? [] },
        });
      } catch (error) {
        notification.error({ message: error?.response?.data?.detail ?? 'Ocurrió un error al cargar los datos de esta pantalla.' });
      }
    },

    *fetchFilesAttachments({ payload }, { call, put, all, select }) {
      yield put({ type: 'show', payload: { isLoadingFiles: true } });

      const { file } = payload;
      const selectedFile = yield select(({ fileDetails }) => fileDetails.file);
      const affiliate = selectedFile.affiliate.id;
      if (selectedFile.odontrogram !== null) {
        try {
          const responses = yield all([
            call(getFileAttachemnts, affiliate, file),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.TICKETS.VIEW)
                ? getFileTicketAttachemnts
                : () => Promise.resolve({ data: [] }),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.RECEIPTS.VIEW)
                ? getFileReceiptAttachemnts
                : () => Promise.resolve({ data: [] }),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.VOUCHERS.VIEW)
                ? getFileVoucherAttachemnts
                : () => Promise.resolve({ data: [] }),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.ODONTOGRAMS.VIEW)
                ? getFileOdontograms
                : () => Promise.resolve({ data: [] }),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermissions([PERMISSIONS.IS_EMPLOYEE, PERMISSIONS.ATTACHMENT_TRAMIT.VIEW])
                ? getTramitAttachments
                : () => Promise.resolve({ data: [] }),
              file,
            ),
          ]);

          const [
            { data: attachments },
            { data: tickets },
            { data: receipts },
            { data: vouchers },
            { data: odontograms },
            { data: affiliatePartialFiles },
          ] = responses;

          yield put({
            type: 'show',
            payload: {
              attachments,
              tickets,
              receipts,
              vouchers,
              odontograms,
              affiliatePartialFiles,
            },
          });
        } catch (error) {
          redirectNotFound(error);
        }
      } else {
        try {
          const responses = yield all([
            call(getFileAttachemnts, affiliate, file),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.TICKETS.VIEW)
                ? getFileTicketAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.RECEIPTS.VIEW)
                ? getFileReceiptAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.VOUCHERS.VIEW)
                ? getFileVoucherAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermissions([PERMISSIONS.IS_EMPLOYEE, PERMISSIONS.ATTACHMENT_TRAMIT.VIEW])
                ? getTramitAttachments
                : () => Promise.resolve([]),
              file,
            ),
          ]);

          const [
            { data: attachments },
            { data: tickets },
            { data: receipts },
            { data: vouchers },
            { data: affiliatePartialFiles },
          ] = responses;

          yield put({
            type: 'show',
            payload: { attachments, tickets, receipts, vouchers, affiliatePartialFiles },
          });
        } catch (error) { }
      }
      yield put({ type: 'show', payload: { isLoadingFiles: false } });
    },

    *fetchFilePrivateAttachments({ payload }, { call, put, all }) {
      yield put({ type: 'show', payload: { isLoadingFiles: true } });
      const { file } = payload;

      try {
        const response = yield all([call(getFilesPrivateAttachments, file)]);
        yield put({
          type: 'show',
          payload: { filePrivateAttachments: response.data },
        });
      } catch (error) { }
      yield put({ type: 'show', payload: { isLoadingFiles: false } });
    },

    *fetchFileResolutions({ payload }, { call, put, all }) {
      const { file } = payload;
      try {
        const response = yield call(getDispositions, file);
        yield put({
          type: 'show',
          payload: { dispositions: response.data.results },
        });
      } catch (error) { console.warn('Error al cargar las disposiciones') }
    },

    *fetchFileAuthorizations({ payload }, { call, put, all }) {
      const { file } = payload;
      try {
        const response = yield call(getAuthorization, file);
        yield put({
          type: 'show',
          payload: { authorizations: response.data.results },
        });
      } catch (error) { console.warn('Error al cargar las disposiciones') }
    },

    *FileResolutionUpload(payload) {
      yield put({
        type: 'setResolution',
        payload: { payload },
      });
    },
    *fileResolutionUpdate(payload) {
      yield put({
        type: 'updateResolution',
        payload: { payload },
      });
    },
    *fetchAddResolutionsResources(payload) {
      yield put({
        type: 'addResource',
        payload: { payload },
      });
    },
    *fetchUpdateResolutionResource(payload) {
      yield put({
        type: 'updateResource',
        payload: { payload },
      });
    },

    *fetchFilesValidations({ payload }, { call, put, all }) {
      yield put({ type: 'show', payload: { isLoadingFiles: true } });
      const { file } = payload;

      try {
        const [response] = yield all([call(getFilesValidations, file)]);

        yield put({
          type: 'show',
          payload: { fileValidations: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
      yield put({ type: 'show', payload: { isLoadingFiles: false } });
    },

    *fetchFilePayments({ payload }, { call, put }) {
      try {
        const response = yield call(getFilePayments, payload);

        yield put({
          type: 'show',
          payload: { payments: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *addTicket({ payload }, { put }) {
      yield put({
        type: 'setTickets',
        payload,
      });
    },

    *addReceipt({ payload }, { put }) {
      yield put({
        type: 'setReceipts',
        payload,
      });
    },
    *addVoucher({ payload }, { put }) {
      yield put({
        type: 'setVouchers',
        payload,
      });
    },
  },

  reducers: {
    show(state, { payload }) {
      return { ...state, ...payload };
    },

    setResolution(state, { payload }) {
      return {
        ...state,
        dispositions: payload.results,

      };
    },

    setAuthorization(state, { payload }) {
      const isNew = !state.authorizations.some(auth => auth.id === payload.id);

      return {
        ...state,
        authorizations: isNew
          ? [payload, ...state.authorizations]
          : state.authorizations.map(auth => auth.id === payload.id ? payload : auth)
      };
    },

    setNewNotification(state, { payload }) {
      const key = payload.target === 'file-scope' ? 'dispositions' : 'authorizations';

      const updatedList = state[key].map((item) => {
        if (item.id === payload.id) {
          return {
            ...item,
            notifications: [payload.notification, ...item.notifications],
          };
        }
        return item;
      });

      return {
        ...state,
        [key]: updatedList,
      };
    },

    partialPaymentChange(state, { payload }) {
      const newCoverages = state.coverages.map((coverage) => {
        if (coverage.id === payload.coverage_id) {
          const newFilePartialPayments = coverage.filepartialpayment.map((payment) =>
            payment.id === payload.payment_id ? payload.response : payment,
          );
          return {
            ...coverage,
            filepartialpayment: newFilePartialPayments,
          };
        }
        return coverage;
      });
      return {
        ...state,
        coverages: newCoverages,
      };
    },

    setNewAnswer(state, { payload }) {
      const { target, notiId, data } = payload;
      const key = target === 'file-scope' ? 'dispositions' : 'authorizations';

      const updatedList = state[key].map((item) => {
        const updatedNotifications = (item.notifications || []).map((notification) => {
          if (notification.id === notiId) {
            return { ...notification, answer: data };
          }
          return notification;
        });
        return { ...item, notifications: updatedNotifications };
      });

      return { ...state, [key]: updatedList };
    },

    updateResolution(state, { payload }) {
      const index = state.dispositions.findIndex((element) => element.id === payload.id);
      const updatedResults = [...state.dispositions];
      updatedResults[index] = payload;
      return { ...state, dispositions: updatedResults };
    },

    addResource(state, { payload }) {
      const { data, id } = payload;
      const newResults = [...state.dispositions];
      newResults.find((e) => e.id === id).resources.push(payload);
      return { ...state, dispositions: { results: newResults } };
    },
    updateResource(state, { payload }) {
      const { data, d_id, r_id } = payload;
      const newResults = [...state.dispositions];
      const dispositionID = newResults.findIndex((disp) => disp.id === d_id);
      const resourceID = newResults[dispositionID].resources.findIndex((reso) => reso.id === r_id);
      newResults[dispositionID].resources[resourceID] = data;
      return { ...state, dispositions: { ...state.dispositions, newResults } };
    },

    setTickets(state, { payload }) {
      return { ...state, tickets: [...state.tickets, payload] };
    },

    setReceipts(state, { payload }) {
      return { ...state, receipts: [...state.receipts, payload] };
    },

    setVouchers(state, { payload }) {
      return { ...state, vouchers: [...state.vouchers, payload] };
    },
  },
};
export default Model;
